import type {ReactNode} from 'react'
import {useStickySidebar} from './hooks/use-sticky-sidebar'
import {BOTTOM_PADDING, TOP_PADDING} from './constants'

interface StickySidebarProps {
  children: ReactNode
  topOffset?: number
  /**
   * Opt in to re-baselining the sticky offset when the scroll container resizes. Needed when the
   * sidebar lives inside a resizable `position: fixed` container (e.g. an issue side sheet), where
   * the default body-resize observer never fires. Leave unset for normal page-scrolled sidebars.
   */
  recalculateOffsetOnResize?: boolean
  className?: string
}

/**
 * A component that creates a sticky sidebar with intelligent scroll direction response
 *
 * - Sticks to the top (with optional offset) when scrolling up
 * - Maintains position at the bottom of the viewport when scrolling down
 * - Uses IntersectionObserver for optimal performance
 */
export function StickySidebar({
  children,
  topOffset = 0,
  recalculateOffsetOnResize = false,
  className,
}: StickySidebarProps) {
  const topWithNotificationShelf = `calc(${topOffset + TOP_PADDING}px + var(--base-sticky-header-height, 0px))`

  const {styles, ref} = useStickySidebar({
    topOffset: topWithNotificationShelf,
    bottomPadding: BOTTOM_PADDING,
    recalculateOffsetOnResize,
  })

  return (
    <div ref={ref} style={styles} data-testid="sticky-sidebar" className={className}>
      {children}
    </div>
  )
}
